import { BrowserRouter, Routes, Route, Outlet, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useAuthStore } from '../stores/authStore';
import { Analytics } from '@alascorporation/seo';
import { siteConfig } from '../config/seo.config';
import VersionTag from '../components/VersionTag';
import DashboardLayout from '../components/layout/DashboardLayout';
import PublicLayout from '../components/layout/PublicLayout';
import ProtectedRoute from '../components/layout/ProtectedRoute';

// Pages
import HomePage from '../pages/HomePage';
import CourseCatalog from '../pages/CourseCatalog';
import CourseDetail from '../pages/CourseDetail';
import Dashboard from '../pages/Dashboard';
import MyCourses from '../pages/MyCourses';
import CertificatePage from '../pages/CertificatePage';
import PaymentResultPage from '../pages/PaymentResultPage';
import ProgramDetailPage from '../pages/ProgramDetailPage';
import AuthCallbackPage from '../pages/AuthCallbackPage';
import ChangePasswordPage from '../pages/ChangePasswordPage';
import GroupCertificatesPage from '../pages/GroupCertificatesPage';
import CccdPage from '../pages/CccdPage';
import CccdCompetitorsPage from '../pages/CccdCompetitorsPage';
import CccdTeamDetailPage from '../pages/CccdTeamDetailPage';
import NotFoundPage from '../pages/NotFoundPage';
import HelpPage from '../pages/HelpPage';
import MyCertificatesPage from '../pages/MyCertificatesPage';

export default function App() {
  const { initialize, isAuthenticated, sessionType } = useAuthStore();
  // /courses es el catálogo público: solo para visitantes sin sesión. Con sesión, una cuenta
  // de usuario va a sus cursos y una sesión de grupo a su Inicio.
  const signedInHome = sessionType === 'group' ? '/dashboard' : '/mis-cursos';

  useEffect(() => {
    initialize();
  }, [initialize]);

  return (
    <BrowserRouter>
      {import.meta.env.PROD && <Analytics config={siteConfig} />}
      <VersionTag />
      <Routes>
        {/* Public One-Page Style Layout */}
        <Route element={<PublicLayout><Outlet /></PublicLayout>}>
          <Route path="/" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <HomePage />} />
          <Route path="/courses" element={isAuthenticated ? <Navigate to={signedInHome} replace /> : <CourseCatalog />} />
          <Route path="/certificates/:code" element={<CertificatePage />} />
          <Route path="/payment/result" element={<PaymentResultPage />} />
          <Route path="/programs/:slug" element={<ProgramDetailPage />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route path="/change-password" element={
            <ProtectedRoute>
              <ChangePasswordPage />
            </ProtectedRoute>
          } />
        </Route>

        {/* Course detail/player: owns the full viewport (fixed sidebars, single
            scrolling column) instead of the marketing navbar/footer chrome. */}
        <Route path="/courses/:slug/:lessonSlug" element={<CourseDetail />} />
        <Route path="/courses/:slug" element={<CourseDetail />} />

        {/* Dashboard/Sidebar Layout */}
        <Route element={<DashboardLayout><Outlet /></DashboardLayout>}>
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="/mis-cursos" element={
            <ProtectedRoute>
              <MyCourses />
            </ProtectedRoute>
          } />
          <Route path="/certificados" element={
            <ProtectedRoute>
              <MyCertificatesPage />
            </ProtectedRoute>
          } />
          <Route path="/ayuda" element={
            <ProtectedRoute>
              <HelpPage />
            </ProtectedRoute>
          } />
          <Route path="/grupo/certificados" element={
            <ProtectedRoute>
              <GroupCertificatesPage />
            </ProtectedRoute>
          } />
          <Route path="/cccd/:eventId?" element={
            <ProtectedRoute requiredTeamStatus="any">
              <CccdPage />
            </ProtectedRoute>
          } />
          <Route path="/cccd/competidores/:eventId?" element={
            <ProtectedRoute requiredTeamStatus="any">
              <CccdCompetitorsPage />
            </ProtectedRoute>
          } />
          <Route path="/cccd/competidores/:eventId/equipos/:teamId" element={
            <ProtectedRoute requiredTeamStatus="any">
              <CccdTeamDetailPage />
            </ProtectedRoute>
          } />
        </Route>

        <Route path="*" element={<PublicLayout><NotFoundPage /></PublicLayout>} />
      </Routes>
    </BrowserRouter>
  );
}
